import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import DashboardAmministratore from './components/DashboardAmministratore';
import DashboardTecnico from './components/DashboardTecnico';
import DashboardSviluppatore from './components/DashboardSviluppatore';
import Settings from './components/Settings';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import './App.css';

// Percorso del link di reset password inviato per email (/reset-password?token=…).
const RESET_PATH = '/reset-password';
// Impostazioni: raggiungibile da ogni ruolo tramite il menu account (nome in
// basso a sinistra), non fa parte di ROLE_NAV perche' non e' un'area per ruolo.
const SETTINGS_PATH = '/impostazioni';

// Sezioni per area funzionale. Ogni voce: { section, label, icon }.
const ADMIN_SECTIONS = [
  { section: 'panoramica', label: 'Panoramica', icon: 'grid' },
  { section: 'codici', label: 'Codici attivazione', icon: 'key' },
  { section: 'dispositivi', label: 'Dispositivi', icon: 'monitor' },
  { section: 'licenze', label: 'Licenze', icon: 'shield' },
  { section: 'scadenziario', label: 'Scadenziario', icon: 'clock' },
  { section: 'riattivazioni', label: 'Riattivazioni', icon: 'refresh' },
  { section: 'admin-password-reset', label: 'Sblocco password', icon: 'key' },
  { section: 'utenti', label: 'Utenti', icon: 'users' },
];
const DEV_SECTIONS = [
  { section: 'panoramica', label: 'Panoramica', icon: 'grid' },
  { section: 'pubblica', label: 'Rilascia versione', icon: 'upload' },
  { section: 'file', label: 'Versioni pubblicate', icon: 'package' },
  { section: 'eventi', label: 'Canale aggiornamenti', icon: 'activity' },
];
const TEC_SECTIONS = [
  { section: 'verifica', label: 'Verifica codice', icon: 'search' },
  { section: 'installer', label: 'Scarica installer', icon: 'download' },
  { section: 'segnala', label: 'Segnala problema', icon: 'alertMsg' },
];

const DASH = {
  amministratore: DashboardAmministratore,
  sviluppatore: DashboardSviluppatore,
  tecnico: DashboardTecnico,
};

// Aggiunge dash/group e un id univoco. Con group (es. superadmin) l'id e'
// prefissato per evitare collisioni tra sezioni omonime (es. "panoramica").
function tag(list, dash, group) {
  return list.map((s) => ({ ...s, dash, group, id: group ? `${dash}:${s.section}` : s.section }));
}

const ROLE_NAV = {
  // L'amministratore vede anche le interfacce del tecnico (le rotte /api/tecnico
  // accettano gia' il ruolo amministratore). Sezioni raggruppate per area.
  amministratore: [
    ...tag(ADMIN_SECTIONS, 'amministratore', 'Amministrazione'),
    ...tag(TEC_SECTIONS, 'tecnico', 'Tecnico'),
  ],
  sviluppatore: tag(DEV_SECTIONS, 'sviluppatore'),
  tecnico: tag(TEC_SECTIONS, 'tecnico'),
  // Superadmin vede tutto: sezioni di tutte le aree, raggruppate.
  superadmin: [
    ...tag(ADMIN_SECTIONS, 'amministratore', 'Amministrazione'),
    ...tag(DEV_SECTIONS, 'sviluppatore', 'Sviluppo'),
    ...tag(TEC_SECTIONS, 'tecnico', 'Tecnico'),
  ],
};

// Indirizzo di una voce di menu: /<area>/<sezione> (es. /amministratore/licenze).
//
// L'area nel percorso NON e' ridondante: "panoramica" esiste sia in area
// Amministrazione sia in area Sviluppo, e il superadmin le vede entrambe — un
// indirizzo piatto tipo /panoramica sarebbe ambiguo.
const pathOf = (item) => `/${item.dash}/${item.section}`;

// La voce di menu corrispondente a un indirizzo, cercata SOLO tra quelle del
// ruolo: e' anche la guardia che impedisce a un tecnico di aprire a mano
// /amministratore/licenze (le API restano comunque protette lato server —
// questa e' solo l'esperienza d'uso, non la sicurezza).
function navItemForPath(role, pathname) {
  const nav = ROLE_NAV[role] || [];
  const [, dash, sectionName] = pathname.split('/');
  return nav.find((s) => s.dash === dash && s.section === sectionName) || null;
}

function getInitialTheme() {
  const saved = localStorage.getItem('hm-theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getInitialSidebarCollapsed() {
  return localStorage.getItem('hm-sidebar-collapsed') === '1';
}

// Chi puo' ricevere ALMENO UN tipo di notifica. Gli amministratori le ricevono
// tutte (segnalazioni, riattivazioni, scadenze...); il tecnico riceve solo
// l'esito delle proprie segnalazioni (vedi lib/notify.js -> recipientIds).
// Deve restare allineato ai destinatari calcolati lato server: qui serve solo
// a non aprire uno stream e non mostrare il campanello a chi non ricevera' mai
// nulla (es. sviluppatore, rivenditore).
const RECEIVES_NOTIFICATIONS = ['amministratore', 'superadmin', 'tecnico'];

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(getInitialTheme);
  const [showForgot, setShowForgot] = useState(false);
  const [authFlash, setAuthFlash] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  // Il default salvato (mostrato in Impostazioni), distinto dallo stato live
  // qui sopra: la freccia sulla sidebar cambia solo il secondo (comodo del
  // momento), Impostazioni cambia entrambi. Vedi setSidebarPref piu' sotto.
  const [sidebarCollapsedSetting, setSidebarCollapsedSetting] = useState(getInitialSidebarCollapsed);
  const [badges, setBadges] = useState({});
  // Centro notifiche: elenco (campanello nell'header) e conteggio delle non lette.
  // Niente avviso a comparsa: l'arrivo si vede solo dal contatore sul campanello,
  // senza un elemento che si sovrapponga al contenuto della pagina.
  // `notifications` resta null per chi non le riceve — cosi' l'header non mostra
  // nemmeno il campanello.
  const [notifications, setNotifications] = useState(null);
  const [unread, setUnread] = useState(0);
  // Incrementato a ogni nuova segnalazione ricevuta via SSE: la dashboard lo usa
  // come chiave di ricarica, cosi' l'elenco si aggiorna da solo se l'admin sta
  // gia' guardando la sezione (senza questo, si aggiornerebbe solo il badge).
  const [supportVersion, setSupportVersion] = useState(0);
  const viewRef = useRef(null);

  // La sezione corrente NON e' piu' uno stato: e' derivata dall'indirizzo. Cosi'
  // l'URL e cio' che si vede non possono divergere, e ricaricare la pagina o
  // condividere il link riporta esattamente dove si era.
  const isResetLink = location.pathname.replace(/\/+$/, '') === RESET_PATH;
  const isSettingsPath = location.pathname.replace(/\/+$/, '') === SETTINGS_PATH;
  const resetToken = isResetLink ? searchParams.get('token') : null;
  const nav = (user && ROLE_NAV[user.role]) || [];
  const current = user ? navItemForPath(user.role, location.pathname) : null;
  const homePath = nav.length ? pathOf(nav[0]) : null;

  // Rigioca l'animazione d'ingresso a ogni cambio sezione SENZA rimontare la
  // vista. Prima qui c'era un `key={current.id}` sul contenitore: otteneva lo
  // stesso effetto, ma cambiare la key fa buttare via a React l'intero
  // sottoalbero e ricrearlo — cioe' la dashboard veniva smontata e rimontata a
  // ogni click nel menu, rieseguendo tutti i suoi fetch. Un'animazione da 0,32s
  // costava quattro interrogazioni al database.
  //
  // useLayoutEffect e non useEffect: deve avvenire PRIMA che il browser
  // disegni. Con useEffect (che gira dopo il paint) la nuova sezione comparirebbe
  // per un istante gia' opaca, per poi ripartire da trasparente: uno sfarfallio.
  useLayoutEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    el.classList.remove('view-enter');
    void el.offsetWidth; // forza un reflow: senza, il browser non riavvia l'animazione
    el.classList.add('view-enter');
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    localStorage.setItem('hm-theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('hm-sidebar-collapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        setUser(data);
        // Preferenze per account: sovrascrivono il default locale (localStorage/
        // matchMedia) con quanto salvato lato server per QUESTO utente.
        if (data.theme === 'light' || data.theme === 'dark') setTheme(data.theme);
        if (typeof data.sidebar_collapsed === 'boolean') {
          setSidebarCollapsed(data.sidebar_collapsed);
          setSidebarCollapsedSetting(data.sidebar_collapsed);
        }
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const handleLogin = (u) => {
    setAuthFlash('');
    setUser(u);
    if (u.theme === 'light' || u.theme === 'dark') setTheme(u.theme);
    if (typeof u.sidebar_collapsed === 'boolean') {
      setSidebarCollapsed(u.sidebar_collapsed);
      setSidebarCollapsedSetting(u.sidebar_collapsed);
    }
    const uNav = ROLE_NAV[u.role];
    if (uNav && uNav.length) navigate(pathOf(uNav[0]), { replace: true });
  };

  // Fine del flusso di reset: toglie il token dall'indirizzo e torna al login,
  // con un avviso di conferma se la password e' stata reimpostata. `replace`:
  // il link col token non deve restare nella cronologia del browser.
  const handleResetDone = ({ success, message }) => {
    setShowForgot(false);
    setAuthFlash(success ? (message || 'Password reimpostata. Ora puoi accedere.') : '');
    navigate('/', { replace: true });
  };

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setUser(null);      // chiude anche lo stream SSE (cleanup dell'effetto)
    setBadges({});
    setNotifications(null);
    setUnread(0);
    navigate('/', { replace: true });
  };

  // Aggiorna una preferenza personale (tema/sidebar) lato server, cosi' segue
  // l'utente su ogni dispositivo/browser invece di restare solo nel
  // localStorage. Fallisce in silenzio: il localStorage resta comunque la
  // sorgente immediata, un errore di rete non deve bloccare l'uso dei toggle.
  const patchPreferences = useCallback((partial) => {
    fetch('/api/auth/preferences', {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    }).then((res) => {
      if (!res.ok) console.warn('[preferences] salvataggio non riuscito');
    }).catch(() => {});
  }, []);

  const setThemePref = useCallback((next) => {
    setTheme(next);
    if (user) patchPreferences({ theme: next });
  }, [user, patchPreferences]);

  // Da Impostazioni: cambia il default salvato E la sidebar visibile ora
  // (anteprima immediata della scelta), a differenza della freccia che
  // tocca solo la seconda. Vedi sidebarCollapsedSetting piu' sopra.
  const setSidebarPref = useCallback((next) => {
    setSidebarCollapsed(next);
    setSidebarCollapsedSetting(next);
    if (user) patchPreferences({ sidebar_collapsed: next });
  }, [user, patchPreferences]);

  // Le notifiche silenziate vivono dentro `user` (non serve rifletterle altrove
  // come per tema/sidebar): aggiornamento ottimistico + salvataggio server.
  const setMutedNotificationTypes = useCallback((next) => {
    setUser((u) => (u ? { ...u, muted_notification_types: next } : u));
    patchPreferences({ muted_notification_types: next });
  }, [patchPreferences]);

  // Comprimere/espandere dalla freccia sulla sidebar e' un comodo momentaneo
  // (piu' spazio schermo ora), non una scelta di preferenza: resta locale a
  // questa sessione/scheda, senza toccare il default salvato in Impostazioni.
  // Il default si cambia solo da li' (onSetSidebarCollapsed -> setSidebarPref),
  // che infatti persiste e viene sempre riapplicato al login/reload.
  const toggleSidebarCollapsed = () => setSidebarCollapsed((c) => !c);

  // Navigare = cambiare indirizzo. La sezione mostrata si ricava da li'.
  const selectSection = useCallback((id) => {
    const target = (user ? ROLE_NAV[user.role] || [] : []).find((s) => s.id === id);
    if (target) navigate(pathOf(target));
    setDrawerOpen(false);
  }, [user, navigate]);

  const openSettings = useCallback(() => {
    navigate(SETTINGS_PATH);
    setDrawerOpen(false);
  }, [navigate]);

  const onBadges = useCallback((b) => setBadges((prev) => ({ ...prev, ...b })), []);

  // Porta alla sezione di una notifica. La stessa sezione puo' stare in aree
  // diverse a seconda del ruolo (per l'admin "segnala" e' sotto l'area Tecnico),
  // percio' si cerca nel menu del ruolo invece di scrivere l'indirizzo a mano.
  const goToSection = useCallback((sectionName) => {
    const target = (user ? ROLE_NAV[user.role] || [] : []).find((s) => s.section === sectionName);
    if (target) navigate(pathOf(target));
    setDrawerOpen(false);
  }, [user, navigate]);

  // Indirizzo che non corrisponde a nessuna voce del menu del ruolo: si viene
  // riportati alla prima sezione disponibile. Copre "/" (appena entrati), un
  // indirizzo scritto male, e un tecnico che prova ad aprire a mano una sezione
  // da amministratore. `replace`: non lascia in cronologia un indirizzo che non
  // porta da nessuna parte, cosi' il tasto "indietro" non ci ritorna sopra.
  useEffect(() => {
    if (!user || loading || isResetLink || isSettingsPath) return;
    if (!current && homePath) navigate(homePath, { replace: true });
  }, [user, loading, isResetLink, isSettingsPath, current, homePath, navigate]);

  // Applica i contatori che arrivano dal server: `unread` alimenta il campanello,
  // `badges` i contatori in sidebar (cose ancora da gestire). Sono due numeri
  // diversi di proposito: una notifica letta non e' una cosa gia' gestita.
  const applyState = useCallback((msg) => {
    if (typeof msg.unread === 'number') setUnread(msg.unread);
    if (msg.badges) setBadges((prev) => ({ ...prev, ...msg.badges }));
  }, []);

  // Elenco iniziale delle notifiche al login/ricarica. Lo stream porta poi gli
  // aggiornamenti, ma da solo non basta: le notifiche arrivate mentre si era
  // offline stanno solo a DB.
  useEffect(() => {
    if (!user || !RECEIVES_NOTIFICATIONS.includes(user.role)) {
      setNotifications(null);
      return;
    }
    let active = true;
    fetch('/api/notifications', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!active) return;
        setNotifications(data.notifications || []);
        applyState(data);
      })
      .catch(() => { if (active) setNotifications([]); });
    return () => { active = false; };
  }, [user, applyState]);

  // Consegna in tempo reale (SSE). L'autenticazione passa dal cookie di sessione:
  // EventSource non permette header custom ma invia i cookie same-origin.
  useEffect(() => {
    if (!user || !RECEIVES_NOTIFICATIONS.includes(user.role)) return;
    if (typeof EventSource === 'undefined') return; // resta il caricamento iniziale

    const es = new EventSource('/api/notifications/stream', { withCredentials: true });

    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      applyState(msg); // presente sia in 'state' sia in 'notification'

      if (msg.type === 'notification' && msg.notification) {
        const n = msg.notification;
        setNotifications((prev) => [n, ...(prev || [])]);
        // Se l'admin sta gia' guardando la sezione, il suo elenco si ricarica.
        if (n.type === 'support_report') setSupportVersion((v) => v + 1);
      }
    };
    // In caso di errore EventSource riconnette da solo, e alla riconnessione il
    // server rimanda subito lo stato: non serve gestirlo a mano.

    return () => es.close();
  }, [user, applyState]);

  // Apertura di una notifica dal pannello del campanello: la segna come letta e
  // porta alla sezione competente. L'aggiornamento locale e' ottimistico, ma il
  // conteggio autorevole arriva comunque dalla risposta del server.
  const openNotification = useCallback((n) => {
    if (n.section) goToSection(n.section);
    setNotifications((prev) =>
      (prev || []).map((x) => (x.id === n.id ? { ...x, read_at: x.read_at || new Date().toISOString() } : x))
    );
    if (!n.read_at) {
      fetch(`/api/notifications/${n.id}/read`, { method: 'POST', credentials: 'include' })
        .then((res) => (res.ok ? res.json() : Promise.reject()))
        .then(applyState)
        .catch(() => { /* il prossimo evento dello stream riallinea i contatori */ });
    }
  }, [goToSection, applyState]);


  // Il link di reset ha la precedenza su tutto (anche prima del check di sessione).
  if (resetToken) {
    return <div className="app"><ResetPassword token={resetToken} onDone={handleResetDone} /></div>;
  }
  if (loading) return <div className="app"><div className="loading">Caricamento...</div></div>;
  if (!user) {
    if (showForgot) {
      return <div className="app"><ForgotPassword onBack={() => setShowForgot(false)} /></div>;
    }
    return <div className="app"><Login onLogin={handleLogin} onForgot={() => setShowForgot(true)} flash={authFlash} /></div>;
  }
  if (user.must_change_password) {
    return <div className="app"><ChangePassword user={user} forced onDone={setUser} onLogout={handleLogout} /></div>;
  }

  // Indirizzo non ancora valido: l'effetto qui sopra sta gia' reindirizzando alla
  // prima sezione. Si evita di disegnare per un istante una vista sbagliata.
  // Impostazioni fa eccezione: non e' in ROLE_NAV, quindi "current" resta null
  // ma l'indirizzo e' valido, non va reindirizzato via.
  if (!current && homePath && !isSettingsPath) return <div className="app"><div className="loading">Caricamento...</div></div>;

  const Comp = current ? DASH[current.dash] : null;

  const view = isSettingsPath
    ? <Settings user={user} theme={theme} onSetTheme={setThemePref} sidebarCollapsed={sidebarCollapsedSetting} onSetSidebarCollapsed={setSidebarPref} onSetMutedTypes={setMutedNotificationTypes} />
    : Comp
      ? <Comp section={current.section} onBadges={onBadges} user={user} supportVersion={supportVersion} />
      : <div className="card"><div className="empty">Nessuna interfaccia disponibile per il ruolo "{user.role}".</div></div>;

  return (
    <div className="app">
      <div className={`app-shell ${drawerOpen ? 'drawer-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar
          sections={nav}
          active={current ? current.id : null}
          onSelect={selectSection}
          badges={badges}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
          onOpenSettings={openSettings}
          user={user}
          onLogout={handleLogout}
        />
        <div className="backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
        <div className="main">
          <Header
            title={isSettingsPath ? 'Impostazioni' : current ? current.label : 'HIPLAAS Manager'}
            subtitle={!isSettingsPath && current && current.group ? current.group : undefined}
            onToggleMenu={() => setDrawerOpen((o) => !o)}
            notifications={notifications}
            unread={unread}
            onOpenNotification={openNotification}
          />
          <main className="content">
            <div className="view-enter" ref={viewRef}>{view}</div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default App;
